import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRightOutlined } from '@ant-design/icons';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { saveIntendedUrl } from '../../../utils/persistentNavigation';
import { trackEvent } from '../../../utils/analytics';
import { useInMarketingShell } from '../../Marketing/MarketingContext';
import useReveal from '../useReveal';
import { BENCHMARK_LABEL } from '../../../content/benchmarkClaim';
import './ComparisonPage.css';

/**
 * ProductHuntPage — /producthunt. Campaign welcome page for the Jul 29, 2026 PH launch.
 * Every link in the PH listing points here with UTMs
 * (?utm_source=producthunt&utm_medium=launch&utm_campaign=launch_jul29 — canonical
 * per /marketing/STRATEGY.md §7, verified on the live listing Jul 25) so
 * launch-day traffic and signup->first-hire conversion are attributable in GA4.
 *
 * Deliberately NOINDEX: this is a campaign page, not an AEO surface — it is not in
 * the sitemap and carries a robots noindex meta. Reuses the cmp-* visual language.
 * Copy follows the positioning canon (updated 2026-08-14): tagline, "solo -> manages 5"
 * (never "promoted"), honest pricing ("start with 200 free credits; usage-based,
 * no monthly range figures in public copy"). Post-v3 (Jul 20): 200 free starter
 * credits exist again and may be referenced. PH follow badge added Jul 20 links
 * to the launch page (followers get notified at launch).
 */

const Section = ({ className = '', label, title, children }) => {
  const [ref, visible] = useReveal();
  return (
    <section className={`aie-section cmp-section ${className}`} ref={ref}>
      <div className={`aie-reveal ${visible ? 'is-visible' : ''}`}>
        {(label || title) && (
          <div className="aie-section-head cmp-section-head">
            {label && <span className="cmp-eyebrow">{label}</span>}
            {title && <h2 className="aie-section-title aie-display">{title}</h2>}
          </div>
        )}
        {children}
      </div>
    </section>
  );
};

const ProductHuntPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { isDarkMode } = useTheme();
  const inMarketingShell = useInMarketingShell();

  const dark = inMarketingShell ? true : isDarkMode;

  useEffect(() => {
    trackEvent('ph_landing_view', { campaign: 'launch_jul29' });
  }, []);

  const handleHire = (loc) => {
    trackEvent('cta_click', { cta_name: 'hire_ai_employee', compare_page: 'producthunt', location: loc });
    if (!currentUser) {
      saveIntendedUrl('/ai_employee');
      navigate('/signup');
      return;
    }
    navigate('/ai_employee');
  };

  return (
    <>
      <Helmet>
        <title>Welcome, Product Hunt! Hire your first AI employee | CellCog</title>
        <meta
          name="description"
          content="CellCog on Product Hunt: hire AI employees for any role. You build the org chart. They manage each other."
        />
        {/* Campaign page: keep out of search indexes; PH traffic arrives by direct link. */}
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className={`aie-landing cmp-page ${dark ? 'dark' : 'light'}`}>
        {/* Hero */}
        <section className="cmp-hero">
          <div className="cmp-hero__glow" aria-hidden="true" />
          <div className="cmp-hero__inner">
            <span className="cmp-hero__pill">
              <span className="cmp-hero__pill-dot" aria-hidden="true" />
              Hello, Product Hunt 👋
            </span>
            <h1 className="aie-display cmp-hero__title">Hire AI employees for any role.</h1>
            <p className="cmp-hero__sub">
              You build the org chart. They manage each other. Describe the role in a prompt; your
              employee names itself, sets up its inbox and task board, and starts working shifts.
            </p>
            <div className="cmp-hero__ctas">
              <button type="button" className="aie-btn aie-btn-primary" onClick={() => handleHire('hero')}>
                Get started <ArrowRightOutlined />
              </button>
            </div>
            <p className="cmp-facts-asof">
              Honest pricing, upfront: start with 200 free credits, no card required. Real work is
              usage-based, and the cost depends purely on how much work you assign.
            </p>
            <a
              href="https://www.producthunt.com/products/cellcog?launch=cellcog&utm_source=badge&utm_medium=badge&utm_campaign=launch_jul29"
              target="_blank"
              rel="noreferrer"
              className="cmp-ph-badge"
              onClick={() => trackEvent('cta_click', { cta_name: 'ph_follow_badge', compare_page: 'producthunt', location: 'hero' })}
            >
              <img
                src={`https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=1200244&theme=${dark ? 'dark' : 'light'}`}
                alt="CellCog on Product Hunt — follow to be notified at launch"
                width="250"
                height="54"
                style={{ marginTop: 16 }}
              />
            </a>
          </div>
        </section>

        {/* Why this is different — the three claims a PH visitor should leave with */}
        <Section label="What you're looking at" title="Employees, not automations">
          <div className="cmp-answer">
            <p className="cmp-answer__p">
              A CellCog AI Employee is not a workflow with a chat UI. Each one has a name, its own
              email inbox, channels, a task board, persistent memory across shifts, and dashboards it keeps
              for you. You talk to it like a colleague; it reports like one.
            </p>
            <p className="cmp-answer__p">
              And employees scale the way people do: our own outbound employee Reese started solo on
              June 27 and now manages a five-rep AI team &mdash; she onboarded them herself. We run our own company on the same AI employees.
            </p>
          </div>
        </Section>

        {/* Proof links */}
        <Section label="The receipts" title="Don't take the tagline's word for it">
          <div className="cmp-related">
            <Link to="/ai-organization" className="cmp-related__link">
              Our real org chart, hire dates included <ArrowRightOutlined />
            </Link>
            <Link to="/benchmarks" className="cmp-related__link">
              {BENCHMARK_LABEL} <ArrowRightOutlined />
            </Link>
            <Link to="/compare/ai-employee-platforms" className="cmp-related__link">
              How we compare to every platform in the category <ArrowRightOutlined />
            </Link>
          </div>
        </Section>

        {/* CTA band */}
        <section className="cmp-cta-band">
          <h2 className="aie-display cmp-cta-band__title">Your upvote is appreciated. Your hire is the real test.</h2>
          <p className="cmp-cta-band__body">
            Describe a role in plain language and watch an AI employee onboard itself. If it doesn&rsquo;t
            impress you in its first shift, tell us why on the launch thread &mdash; the founder reads
            every comment.
          </p>
          <button type="button" className="cmp-cta-band__btn" onClick={() => handleHire('cta_band')}>
            Get started <ArrowRightOutlined />
          </button>
        </section>
      </div>
    </>
  );
};

export default ProductHuntPage;
